import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbCheck, TbChevronDown, TbWorld } from "react-icons/tb";

const LANGUAGES: {
    [key: string]: { label: string; flag: string }
} = {
    ar: { label: "Arabic", flag: "🇱🇾" },
    en: { label: "English", flag: "🇬🇧" }
}

interface LangMenuProp {
    className?: string;
}

const LangMenu = ({ className = "" }: LangMenuProp) => {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const changeLanguageHandle = (lang: string) => {
        i18n.changeLanguage(lang);
        setOpen(false);
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 0);
    }

    const currentLang = LANGUAGES[i18n.language] || { label: i18n.language.toUpperCase(), flag: "" };

    return (
        <div ref={ref} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="Language Selector"
                aria-haspopup="listbox"
                aria-expanded={open}
                className="flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-2 text-[0.9375rem] font-light text-white backdrop-blur transition hover:border-primary-300 hover:bg-white/10"
            >
                <TbWorld className="h-[18px] w-[18px] text-primary-200" />
                <span className="text-base leading-none">{currentLang.flag}</span>
                {currentLang.label}
                <TbChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <ul
                    role="listbox"
                    className="absolute bottom-full start-0 z-20 mb-2 min-w-[170px] animate-fade-in rounded-2xl bg-white p-1.5 shadow-card-hover ring-1 ring-black/5"
                >
                    {Object.entries(LANGUAGES).map(([code, { label, flag }]) => {
                        const active = i18n.language === code;
                        return (
                            <li key={code}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    onClick={() => changeLanguageHandle(code)}
                                    className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-start text-[0.9375rem] font-light tracking-brand transition ${active ? "bg-primary-50 text-primary-800" : "text-gray-700 hover:bg-primary-400 hover:text-white"}`}
                                >
                                    <span className="text-lg leading-none">{flag}</span>
                                    <span className="flex-1">{label}</span>
                                    {active && <TbCheck className="h-4 w-4" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    )
}

export default LangMenu;
